import { useEffect, useMemo, useState } from "react"

import { useMutation, useQuery } from "@apollo/client"

import { useErrorNotification } from "@/souhl_admin/components/ui/ErrorNotification/ErrorNotification"
import { getFragmentData } from "@/souhl_admin/gql/__generated__"
import { graphql } from "@/souhl_admin/gql/__generated__/gql"

const FeatureFlagTabEditStateFragment = graphql(/* GraphQL */ `
	fragment FeatureFlagTabEditState on CompanyFeatureFlag {
		featureFlag {
			id
		}
		isCompanyWideEnabled
		enabledSalonIds
	}
`)

const FeatureFlagTabSalonIdsFragment = graphql(/* GraphQL */ `
	fragment FeatureFlagTabSalonIds on Salon {
		id
	}
`)

const GetCompanyFeatureFlags = graphql(/* GraphQL */ `
	query GetCompanyFeatureFlags($param: CompanyFeatureFlagsParam!) {
		companyFeatureFlags(param: $param) {
			companyFeatureFlags {
				...FeatureFlagTabEditState
				...FeatureFlagTab
			}
		}
	}
`)

const GetCompanySalons = graphql(/* GraphQL */ `
	query GetCompanySalonsForFeatureFlag($param: CompanyParam!) {
		company(param: $param) {
			company {
				salons {
					...FeatureFlagTabSalonIds
					...FeatureFlagTabSalon
				}
			}
		}
	}
`)

const ToggleFeatureFlags = graphql(/* GraphQL */ `
	mutation ToggleFeatureFlags($input: ToggleFeatureFlagsInput!) {
		toggleFeatureFlags(input: $input) {
			companyFeatureFlags {
				...FeatureFlagTabEditState
				...FeatureFlagTab
			}
		}
	}
`)

export type FlagEditState = {
	companyWide: boolean
	enabledSalonIds: number[]
}

const isFlagChanged = (
	state: FlagEditState,
	editState: {
		isCompanyWideEnabled: boolean
		enabledSalonIds: readonly number[]
	}
): boolean => {
	if (state.companyWide !== editState.isCompanyWideEnabled) return true
	if (state.enabledSalonIds.length !== editState.enabledSalonIds.length)
		return true
	const orig = [...editState.enabledSalonIds].sort((a, b) => a - b)
	const curr = [...state.enabledSalonIds].sort((a, b) => a - b)
	return orig.some((id, i) => id !== curr[i])
}

export const useFeatureFlagTab = (companyId: string) => {
	const { addError } = useErrorNotification()
	const [userEdits, setUserEdits] = useState<Record<string, FlagEditState>>({})
	const [saving, setSaving] = useState(false)
	const [openDropdownFlagId, setOpenDropdownFlagId] = useState<string | null>(
		null
	)

	const { data, loading } = useQuery(GetCompanyFeatureFlags, {
		variables: { param: { companyId } },
		fetchPolicy: "cache-and-network",
	})

	const { data: salonsData } = useQuery(GetCompanySalons, {
		variables: { param: { id: companyId } },
		fetchPolicy: "cache-first",
	})

	const [toggleFeatureFlags] = useMutation(ToggleFeatureFlags, {
		refetchQueries: ["GetCompanyFeatureFlags", "GetCompanies"],
		awaitRefetchQueries: true,
	})

	const companyFeatureFlagsRaw = useMemo(
		() => data?.companyFeatureFlags.companyFeatureFlags ?? [],
		[data]
	)
	const salonsRaw = useMemo(
		() => salonsData?.company.company?.salons ?? [],
		[salonsData]
	)

	const editStates = useMemo(
		() =>
			companyFeatureFlagsRaw.map((raw) =>
				getFragmentData(FeatureFlagTabEditStateFragment, raw)
			),
		[companyFeatureFlagsRaw]
	)
	const salonIds = useMemo(
		() =>
			salonsRaw.map(
				(raw) => getFragmentData(FeatureFlagTabSalonIdsFragment, raw).id
			),
		[salonsRaw]
	)

	const flagStates = useMemo(() => {
		const result: Record<string, FlagEditState> = {}
		for (const es of editStates) {
			result[es.featureFlag.id] = userEdits[es.featureFlag.id] ?? {
				companyWide: es.isCompanyWideEnabled,
				enabledSalonIds: [...es.enabledSalonIds],
			}
		}
		return result
	}, [editStates, userEdits])

	useEffect(() => {
		if (openDropdownFlagId === null) return
		const clickHandler = () => setOpenDropdownFlagId(null)
		const keyHandler = (e: KeyboardEvent) => {
			if (e.key === "Escape") setOpenDropdownFlagId(null)
		}
		document.addEventListener("click", clickHandler)
		document.addEventListener("keydown", keyHandler)
		return () => {
			document.removeEventListener("click", clickHandler)
			document.removeEventListener("keydown", keyHandler)
		}
	}, [openDropdownFlagId])

	const isDirty = useMemo(() => {
		return editStates.some((es) => {
			const state = flagStates[es.featureFlag.id]
			if (!state) return false
			return isFlagChanged(state, es)
		})
	}, [flagStates, editStates])

	const getBaseState = (flagId: string): FlagEditState | null => {
		const es = editStates.find((e) => e.featureFlag.id === flagId)
		if (!es) return null
		return {
			companyWide: es.isCompanyWideEnabled,
			enabledSalonIds: [...es.enabledSalonIds],
		}
	}

	const handleCompanyWideToggle = (flagId: string, enabled: boolean) => {
		setUserEdits((prev) => {
			const base = prev[flagId] ?? getBaseState(flagId)
			if (!base) return prev
			return {
				...prev,
				[flagId]: {
					companyWide: enabled,
					enabledSalonIds: base.enabledSalonIds,
				},
			}
		})
		setOpenDropdownFlagId(null)
	}

	const handleSalonToggle = (flagId: string, salonId: number) => {
		setUserEdits((prev) => {
			const base = prev[flagId] ?? getBaseState(flagId)
			if (!base) return prev
			const exists = base.enabledSalonIds.includes(salonId)
			let nextSalonIds: number[]
			if (exists) {
				nextSalonIds = base.enabledSalonIds.filter((id) => id !== salonId)
			} else {
				nextSalonIds = [...base.enabledSalonIds, salonId]
			}
			return {
				...prev,
				[flagId]: {
					...base,
					enabledSalonIds: nextSalonIds,
				},
			}
		})
	}

	const handleBulkToggle = (flagId: string) => {
		setUserEdits((prev) => {
			const base = prev[flagId] ?? getBaseState(flagId)
			if (!base) return prev
			const allEnabled =
				salonIds.length > 0 &&
				salonIds.every((id) => base.enabledSalonIds.includes(id))
			let nextSalonIds: number[]
			if (allEnabled) {
				nextSalonIds = []
			} else {
				nextSalonIds = [...salonIds]
			}
			return {
				...prev,
				[flagId]: {
					...base,
					enabledSalonIds: nextSalonIds,
				},
			}
		})
	}

	const handleToggleDropdown = (flagId: string) => {
		if (openDropdownFlagId === flagId) {
			setOpenDropdownFlagId(null)
		} else {
			setOpenDropdownFlagId(flagId)
		}
	}

	const handleSave = () => {
		if (saving) return
		const changedEditStates = editStates.filter((es) => {
			const state = flagStates[es.featureFlag.id]
			if (!state) return false
			return isFlagChanged(state, es)
		})
		if (changedEditStates.length === 0) return

		setSaving(true)
		toggleFeatureFlags({
			variables: {
				input: {
					companyId,
					featureFlags: changedEditStates.map((es) => {
						const state = flagStates[es.featureFlag.id]
						// companyWide=true の場合 backend は enabledSalonIds を無視して全店舗 ON にする
						// (schema: "isCompanyWideEnabled が false の場合のみ使用、true の場合は無視")
						let enabledSalonIds: number[]
						if (state?.companyWide) {
							enabledSalonIds = []
						} else {
							enabledSalonIds = state?.enabledSalonIds ?? []
						}
						return {
							featureFlagId: es.featureFlag.id,
							isCompanyWideEnabled: state?.companyWide ?? false,
							enabledSalonIds,
						}
					}),
				},
			},
		})
			.then(() => {
				setUserEdits({})
			})
			.catch((error) => {
				console.error("Feature Flag の更新に失敗しました:", error)
				addError("Feature Flag の更新に失敗しました")
			})
			.finally(() => {
				setSaving(false)
			})
	}

	return {
		companyFeatureFlags: companyFeatureFlagsRaw,
		salons: salonsRaw,
		flagStates,
		isDirty,
		saving,
		isInitialLoading: loading && !data,
		openDropdownFlagId,
		handleToggleDropdown,
		handleCompanyWideToggle,
		handleSalonToggle,
		handleBulkToggle,
		handleSave,
	}
}
